import clsx from 'clsx'
import { type ChangeEvent, useEffect, useState } from 'react'
import type { FieldError } from 'react-hook-form'
import { Show } from '@/components/show'
import { useDebounce } from '@/hooks/ui/use-debounce'
import { TextInput } from '../text-input'

interface TextViewOrEditProps {
  editable: boolean
  value: string
  onChange?: (value: string) => void
  onBlur?: (event: React.FocusEvent<HTMLInputElement, Element>) => void
  clearRegex?: RegExp
  placeholder?: string
  error?: FieldError
}

export const TextViewOrEdit = ({
  editable,
  value,
  onChange,
  onBlur,
  clearRegex,
  error,
  placeholder
}: TextViewOrEditProps) => {
  const [localValue, setLocalValue] = useState<string>(value)
  const valueDebounced = useDebounce(localValue, 500)

  const handleLocalValue = (event: ChangeEvent<HTMLInputElement>) => {
    let clearValue = event.target.value
    if (clearRegex) {
      clearValue = clearValue.replace(clearRegex, '')
    }

    setLocalValue(clearValue)
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (value !== localValue) {
      if (onChange) {
        onChange(localValue ?? '')
      }
    }
  }, [valueDebounced])

  useEffect(() => {
    setLocalValue(value)
  }, [value])

  return (
    <div className="w-full bg-backgroun flex items-center">
      <Show when={editable}>
        <TextInput
          onBlur={onBlur}
          onChange={handleLocalValue}
          value={localValue}
          className={clsx('h-11', { 'border-destructive': error })}
          placeholder={placeholder}
        />
      </Show>
      <Show when={!editable}>
        <div className="w-full border-b border-ring/50 h-11 flex items-center text-base px-2 py-2">
          <p>{localValue}</p>
        </div>
      </Show>
    </div>
  )
}
